import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from 'flowbite-react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from "../../redux";
import { search, resetSearch } from "../../redux/ducks/search";
import SearchBar from "../SearchBar/SearchBar";
import SearchResult from "../SearchResult/SearchResult";
import { buildChangesOverviewPath } from "../../utils/searchFilters";
import _ from 'lodash';
import {useTranslation} from "react-i18next";
import { Icon } from '../Icon';

interface SearchModalProps {
    visible: boolean;
    onClose: () => void;
}

const SearchModal: React.FC<SearchModalProps> = ({ visible, onClose }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const dispatch = useAppDispatch();
    const lang = i18n.language ?? "fr";

    const runSearch = useCallback(_.debounce((nextQuery: string, nextLang: string) => {
        const trimmed = nextQuery.trim();
        if (trimmed.length >= 3) {
            dispatch(search(trimmed, nextLang));
        } else {
            dispatch(resetSearch());
        }
    }, 500), [dispatch]);

    useEffect(() => {
        return () => {
            runSearch.cancel();
        };
    }, [runSearch]);

    useEffect(() => {
        if (!visible) {
            setQuery("");
            dispatch(resetSearch());
            runSearch.cancel();
        }
    }, [visible, dispatch, runSearch]);

    const onQueryChange = (nextQuery: string) => {
        setQuery(nextQuery);
        runSearch(nextQuery, lang);
    };

    const goToAdvancedSearch = () => {
        const path = buildChangesOverviewPath(query, true);
        onClose();
        navigate(path);
    };

    return (
        <Modal show={visible} onClose={onClose} className="items-start" dismissible size="2xl">
            <Modal.Header>{t('search.title') || 'Recherche'}</Modal.Header>
            <Modal.Body>
                <form onSubmit={(e) => e.preventDefault()}>
                    <SearchBar query={query} onQueryChange={onQueryChange} />
                </form>

                <button
                    type="button"
                    className="mt-3 flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    onClick={goToAdvancedSearch}
                >
                    <Icon type="list" size={4} />
                    {t('search.advanced_filters')}
                    <span className="text-gray-400 dark:text-gray-500">→</span>
                    <span className="font-normal text-gray-500 dark:text-gray-400">
                        {t('search.open_on_changes_page')}
                    </span>
                </button>

                <SearchResult query={query} hasFilters={false} onClose={onClose} />
            </Modal.Body>
        </Modal>
    );
};

export default SearchModal;
